import React from 'react';
import { OptimizeResponse, RouteCombination, RecommendedStrategy } from '../types';

interface Props {
  result: OptimizeResponse;
}

const modeEmoji: Record<string, string> = {
  walking: '🚶',
  transit: '🚌',
  driving: '🚗',
  bicycling: '🚲',
};

function formatDuration(seconds: number): string {
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  return `${hours}h ${remainingMins}min`;
}

const CombinationCard: React.FC<{
  combination: RouteCombination;
  strategy?: RecommendedStrategy;
  isBest: boolean;
}> = ({ combination, strategy, isBest }) => (
  <div className={`combination-card ${isBest ? 'best' : ''}`}>
    {isBest && <div className="best-badge">⭐ Migliore</div>}
    {strategy && !isBest && (
      <div className="alt-badge">#{strategy.rank} Alternativa</div>
    )}

    <h3 className="combo-title">{combination.description}</h3>

    <div className="combo-time">
      <span className="time-icon">⏱️</span>
      <span className="time-value">{formatDuration(combination.totalDurationSeconds)}</span>
    </div>

    {combination.meetingPoint && (
      <div className="meeting-point">
        📍 Punto di incontro: {combination.meetingPoint}
      </div>
    )}

    <div className="legs-list">
      {combination.legs.map((leg, i) => (
        <div key={i} className="leg-item">
          <span className="leg-mode">{modeEmoji[leg.mode] || '🔹'}</span>
          <div className="leg-details">
            <div className="leg-route">
              {leg.origin} → {leg.destination}
            </div>
            <div className="leg-info">
              {leg.durationText} · {leg.distanceText}
            </div>
          </div>
        </div>
      ))}
    </div>

    {strategy && (
      <div className="strategy-reasoning">
        <p>{strategy.reasoning}</p>
        {strategy.summary && (
          <p className="strategy-summary">💡 {strategy.summary}</p>
        )}
      </div>
    )}
  </div>
);

const ResultsPanel: React.FC<Props> = ({ result }) => {
  const { strategy, combinations } = result;

  const bestCombo = combinations.find(c => c.id === strategy.best.combinationId);
  const altCombos = strategy.alternatives
    .map(alt => ({
      combo: combinations.find(c => c.id === alt.combinationId),
      strategy: alt,
    }))
    .filter(a => a.combo);

  return (
    <div className="results-panel">
      <h2>🎯 Risultati</h2>

      {bestCombo && (
        <CombinationCard
          combination={bestCombo}
          strategy={strategy.best}
          isBest={true}
        />
      )}

      {altCombos.length > 0 && (
        <>
          <h3 className="alternatives-title">Alternative</h3>
          {altCombos.map(({ combo, strategy: altStrategy }) => (
            <CombinationCard
              key={combo!.id}
              combination={combo!}
              strategy={altStrategy}
              isBest={false}
            />
          ))}
        </>
      )}

      {strategy.generalAdvice && (
        <div className="general-advice">
          <h3>💬 Consigli generali</h3>
          <p>{strategy.generalAdvice}</p>
        </div>
      )}

      <details className="raw-data">
        <summary>📊 Tutte le combinazioni calcolate ({combinations.length})</summary>
        <div className="all-combinations">
          {combinations
            .sort((a, b) => a.totalDurationSeconds - b.totalDurationSeconds)
            .map(combo => (
              <div key={combo.id} className="mini-combo">
                <span className="mini-combo-desc">{combo.description}</span>
                <span className="mini-combo-time">
                  {formatDuration(combo.totalDurationSeconds)}
                </span>
              </div>
            ))}
        </div>
      </details>
    </div>
  );
};

export default ResultsPanel;
